import React, { useState, useEffect } from 'react';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { AdminQueueState } from './ui/AdminQueueState';

type PlatformRole = 'user' | 'super_admin';
type FilterKey = 'all' | 'user' | 'super_admin' | 'organizer' | 'judge';

interface User {
  id: number;
  username: string;
  email: string;
  role: PlatformRole | string;
  can_organize?: boolean | number;
  can_judge?: boolean | number;
  first_name: string;
  last_name: string;
  created_at: string;
  last_login: string | null;
  vehicle_count: number;
  modification_count: number;
  is_active: boolean;
  avatar_url?: string;
}

interface BulkAction {
  type: 'role_change' | 'capability' | 'delete' | 'activate' | 'deactivate';
  target_role?: PlatformRole;
  can_organize?: boolean;
  can_judge?: boolean;
}

const ROLE_OPTIONS: { value: PlatformRole; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'super_admin', label: 'Super Admin' },
];

const FILTER_OPTIONS: { value: FilterKey; label: string }[] = [
  { value: 'all', label: 'All users' },
  { value: 'user', label: 'Users' },
  { value: 'super_admin', label: 'Super Admins' },
  { value: 'organizer', label: 'Can organize' },
  { value: 'judge', label: 'Can judge' },
];

const formatRoleLabel = (role: string): string =>
  ROLE_OPTIONS.find((r) => r.value === role)?.label ?? role;

const getRoleBadgeColor = (role: string): string => {
  if (role === 'super_admin') return 'bg-orange-600';
  return 'bg-[#1a1a1a]';
};

const hasCap = (value: boolean | number | undefined): boolean =>
  value === true || value === 1;

export const UserManagement: React.FC = () => {
  const { showToast } = useToast();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [pagination, setPagination] = useState<any>(null);
  
  // Filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [pendingRole, setPendingRole] = useState<PlatformRole>('user');
  const [pendingOrganize, setPendingOrganize] = useState(false);
  const [pendingJudge, setPendingJudge] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Bulk operations
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  
  // User profile quick view
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [showUserProfile, setShowUserProfile] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined,
        role_filter: roleFilter !== 'all' ? roleFilter : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      const data = await apiService.getAdminUsers(params);
      setUsers(data.data || []);
      setPagination(data.pagination);
      setStatistics(data.statistics);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when list filters change
  }, [currentPage, searchTerm, roleFilter, sortBy, sortOrder]);

  const handleRoleChange = async (userId: number) => {
    try {
      await apiService.updateUser(userId, {
        role: pendingRole,
        can_organize: pendingOrganize,
        can_judge: pendingJudge,
      });
      await loadUsers();
      showToast('User updated.', 'success');
      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Failed to update user:', err);
      showToast(err.message || 'Unable to update user.', 'error');
    }
  };

  const openRoleModal = (user: User) => {
    setSelectedUser(user);
    setPendingRole((user.role === 'super_admin' ? 'super_admin' : 'user') as PlatformRole);
    setPendingOrganize(hasCap(user.can_organize));
    setPendingJudge(hasCap(user.can_judge));
    setShowRoleModal(true);
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await apiService.deleteUser(userId);
      await loadUsers(); // Reload users
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      showToast(err.message || 'Unable to delete user.', 'error');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const canModifyUser = (user: User) => {
    // Can't modify yourself or user ID 1 (root admin)
    return user.id !== currentUser?.id && user.id !== 1;
  };

  const handleBulkAction = (action: BulkAction) => {
    if (selectedUsers.size === 0) {
      showToast('Select users first.', 'warning');
      return;
    }
    setBulkAction(action);
    setShowBulkConfirm(true);
  };

  const executeBulkAction = async () => {
    if (!bulkAction || selectedUsers.size === 0) return;

    try {
      const userIds = Array.from(selectedUsers);
      
      switch (bulkAction.type) {
        case 'role_change':
          if (bulkAction.target_role) {
            await Promise.all(
              userIds.map((userId) =>
                apiService.updateUser(userId, { role: bulkAction.target_role })
              )
            );
          }
          break;
        case 'capability':
          await Promise.all(
            userIds.map((userId) =>
              apiService.updateUser(userId, {
                ...(bulkAction.can_organize !== undefined
                  ? { can_organize: bulkAction.can_organize }
                  : {}),
                ...(bulkAction.can_judge !== undefined
                  ? { can_judge: bulkAction.can_judge }
                  : {}),
              })
            )
          );
          break;
        case 'delete':
          await Promise.all(
            userIds.map(userId => apiService.deleteUser(userId))
          );
          break;
        case 'activate':
        case 'deactivate':
          throw new Error(`${bulkAction.type} is not implemented yet`);
      }

      setSelectedUsers(new Set());
      setShowBulkConfirm(false);
      setBulkAction(null);
      loadUsers();
    } catch (err: any) {
      console.error('Bulk action failed:', err);
      showToast(err.message || 'Bulk action failed.', 'error');
    }
  };

  const toggleUserSelection = (userId: number) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const toggleSelectAll = () => {
    if (!users || users.length === 0) return;
    
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.id)));
    }
  };

  const getBulkActionText = (action: BulkAction): string => {
    switch (action.type) {
      case 'role_change':
        return `Change role to ${action.target_role} for ${selectedUsers.size} users`;
      case 'capability':
        if (action.can_organize !== undefined) {
          return `${action.can_organize ? 'Grant' : 'Revoke'} organizer access for ${selectedUsers.size} users`;
        }
        if (action.can_judge !== undefined) {
          return `${action.can_judge ? 'Grant' : 'Revoke'} judge access for ${selectedUsers.size} users`;
        }
        return `Update capabilities for ${selectedUsers.size} users`;
      case 'delete':
        return `Delete ${selectedUsers.size} users`;
      case 'activate':
        return `Activate ${selectedUsers.size} users`;
      case 'deactivate':
        return `Deactivate ${selectedUsers.size} users`;
      default:
        return 'Confirm bulk action';
    }
  };

  const handleViewUser = (user: User) => {
    setViewingUser(user);
    setShowUserProfile(true);
  };

  if (loading && (!users || users.length === 0)) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error && (!users || users.length === 0)) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Admin" title="User management" description="Manage accounts, roles, and capabilities." />
        <AdminQueueState
          variant="error"
          title="Could not load users"
          description={error}
          onRetry={() => void loadUsers()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <PageHeader eyebrow="Admin" title="User management" description="Manage accounts, roles, and capabilities." />
        </div>
        <button
          onClick={loadUsers}
          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-lg p-4 border border-white/[0.06] bg-[#0c0c0c]">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Total Users</h3>
            <p className="text-2xl font-bold text-white">{statistics.total_users}</p>
          </div>
          <div className="rounded-lg p-4 border border-white/[0.06] bg-[#0c0c0c]">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Super Admins</h3>
            <p className="text-2xl font-bold text-orange-400">{statistics.super_admin_count}</p>
          </div>
          <div className="rounded-lg p-4 border border-white/[0.06] bg-[#0c0c0c]">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Regular Users</h3>
            <p className="text-2xl font-bold text-blue-400">{statistics.total_users - statistics.super_admin_count}</p>
          </div>
          <div className="rounded-lg p-4 border border-white/[0.06] bg-[#0c0c0c]">
            <h3 className="text-sm font-medium text-gray-400 mb-1">New (30 days)</h3>
            <p className="text-2xl font-bold text-green-400">{statistics.new_users_30d}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg p-6 border border-white/[0.06] bg-[#0c0c0c]">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#161616] border border-white/[0.10] rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-orange-500"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-[#161616] border border-white/[0.10] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
          >
            {FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field as typeof sortBy);
              setSortOrder(order as 'asc' | 'desc');
            }}
            className="bg-[#161616] border border-white/[0.10] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-orange-500"
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="username-asc">Username A-Z</option>
            <option value="username-desc">Username Z-A</option>
            <option value="last_login-desc">Last Login</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedUsers.size > 0 && (
        <div className="bg-orange-900/15 border border-orange-600/30 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center">
              <span className="text-orange-400 font-medium">
                {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkAction({ type: 'capability', can_organize: true })}
                className="bg-blue-600/20 border border-blue-600/30 text-blue-400 px-3 py-1 rounded text-sm hover:bg-blue-600/30 transition-colors"
              >
                Grant Organizer
              </button>
              <button
                onClick={() => handleBulkAction({ type: 'capability', can_judge: true })}
                className="bg-amber-600/20 border border-amber-600/30 text-amber-400 px-3 py-1 rounded text-sm hover:bg-amber-600/30 transition-colors"
              >
                Grant Judge
              </button>
              <button
                onClick={() => handleBulkAction({ type: 'role_change', target_role: 'super_admin' })}
                className="bg-yellow-600/20 border border-yellow-600/30 text-yellow-400 px-3 py-1 rounded text-sm hover:bg-yellow-600/30 transition-colors"
              >
                Set Super Admin
              </button>
              <button
                onClick={() => handleBulkAction({ type: 'role_change', target_role: 'user' })}
                className="bg-white/[0.04] border border-white/[0.08] text-gray-400 px-3 py-1 rounded text-sm hover:bg-white/[0.05] transition-colors"
              >
                Set User
              </button>
              <button
                onClick={() => handleBulkAction({ type: 'delete' })}
                className="bg-red-600/20 border border-red-600/30 text-red-400 px-3 py-1 rounded text-sm hover:bg-red-600/30 transition-colors"
              >
                Delete Selected
              </button>
              <button
                onClick={() => setSelectedUsers(new Set())}
                className="bg-white/[0.04] border border-white/[0.08] text-gray-400 px-3 py-1 rounded text-sm hover:bg-white/[0.05] transition-colors"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      {!loading && users.length === 0 ? (
        <AdminQueueState
          variant={searchTerm || roleFilter !== 'all' ? 'filter-empty' : 'empty'}
          title={
            searchTerm || roleFilter !== 'all'
              ? 'No users match your search or filter'
              : 'No users in the system'
          }
          description={
            searchTerm || roleFilter !== 'all'
              ? 'Try a different search term or role filter.'
              : 'Registered accounts will appear here.'
          }
          onClearFilter={
            searchTerm || roleFilter !== 'all'
              ? () => {
                  setSearchTerm('');
                  setRoleFilter('all');
                  setCurrentPage(1);
                }
              : undefined
          }
          clearFilterLabel="Clear search and filters"
        />
      ) : (
      <div className="card-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#161616]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={users && users.length > 0 && selectedUsers.size === users.length}
                    onChange={toggleSelectAll}
                    className="rounded border-white/[0.10] text-orange-500 focus:ring-orange-500 focus:ring-offset-gray-800"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#141414] divide-y divide-white/[0.08]">
              {(users || []).map((user) => (
                <tr key={user.id} className="hover:bg-[#161616]/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      disabled={user.id === 1 || user.id === currentUser?.id}
                      className="rounded border-white/[0.10] text-orange-500 focus:ring-orange-500 focus:ring-offset-gray-800 disabled:opacity-50"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.avatar_url ? (
                          <img className="h-10 w-10 rounded-full object-cover" src={apiService.getFileUrl(user.avatar_url) || user.avatar_url} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center">
                            <span className="text-white font-medium">
                              {user.username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="text-sm font-medium text-orange-400 hover:text-orange-300 cursor-pointer"
                        >
                          {user.username}
                        </button>
                        <div className="text-sm text-gray-400">{user.email}</div>
                        {user.first_name && user.last_name && (
                          <div className="text-xs text-gray-500">
                            {user.first_name} {user.last_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getRoleBadgeColor(user.role)}`}>
                        {formatRoleLabel(user.role === 'super_admin' ? 'super_admin' : 'user')}
                      </span>
                      {hasCap(user.can_organize) ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white bg-blue-600">
                          Organizer
                        </span>
                      ) : null}
                      {hasCap(user.can_judge) ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white bg-amber-600">
                          Judge
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    <div>Joined: {formatDate(user.created_at)}</div>
                    <div>Last login: {formatDate(user.last_login)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    <div>{user.vehicle_count} vehicles</div>
                    <div>{user.modification_count} modifications</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {canModifyUser(user) && (
                        <>
                          <button
                            onClick={() => openRoleModal(user)}
                            className="text-orange-400 hover:text-orange-300 transition-colors"
                          >
                            Change Access
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteModal(true);
                            }}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {!canModifyUser(user) && (
                        <span className="text-gray-500">Protected</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Pagination */}
      {!loading && users.length > 0 && pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="bg-[#161616] hover:bg-white/[0.06] disabled:bg-[#141414] disabled:text-gray-500 text-white px-3 py-2 rounded-lg transition-colors"
            >
              Previous
            </button>
            <span className="bg-[#161616] text-white px-3 py-2 rounded-lg">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
              className="bg-[#161616] hover:bg-white/[0.06] disabled:bg-[#141414] disabled:text-gray-500 text-white px-3 py-2 rounded-lg transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Access Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => { setShowRoleModal(false); setSelectedUser(null); }}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="heading-card mb-4">
              Change access
            </h3>
            <p className="text-muted mb-4 text-sm">
              Update platform role and capabilities for{' '}
              <strong className="text-white">{selectedUser.username}</strong>.
            </p>
            <label className="block text-sm text-gray-400 mb-1">Platform role</label>
            <select
              value={pendingRole}
              onChange={(e) => setPendingRole(e.target.value as PlatformRole)}
              className="input mb-4"
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-3 text-sm text-gray-200">
                <input
                  type="checkbox"
                  checked={pendingOrganize}
                  onChange={(e) => setPendingOrganize(e.target.checked)}
                  className="rounded border-white/[0.10] text-orange-500 focus:ring-orange-500"
                />
                Can organize (events & clubs)
              </label>
              <label className="flex items-center gap-3 text-sm text-gray-200">
                <input
                  type="checkbox"
                  checked={pendingJudge}
                  onChange={(e) => setPendingJudge(e.target.checked)}
                  className="rounded border-white/[0.10] text-orange-500 focus:ring-orange-500"
                />
                Can judge
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRoleChange(selectedUser.id)}
                className="btn-primary"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="heading-card mb-4">Confirm Delete</h3>
            <p className="text-muted mb-4 text-sm">
              Are you sure you want to delete user <strong className="text-white">{selectedUser.username}</strong>? 
              This will also remove all their vehicles and modifications.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(selectedUser.id)}
                className="btn-danger"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Modal */}
      {showBulkConfirm && bulkAction && (
        <div className="modal-overlay" onClick={() => { setShowBulkConfirm(false); setBulkAction(null); }}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="heading-card mb-4">Confirm Bulk Action</h3>
            <p className="text-muted mb-4 text-sm">
              {getBulkActionText(bulkAction)}
            </p>
            {bulkAction.type === 'delete' && (
              <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-3 mb-4">
                <p className="text-red-400 text-sm">
                  This will permanently delete all selected users and their associated data 
                  (vehicles, modifications, images). This action cannot be undone.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkConfirm(false);
                  setBulkAction(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={executeBulkAction}
                className={bulkAction.type === 'delete' ? 'btn-danger' : 'btn-primary'}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Profile Quick View Modal */}
      {showUserProfile && viewingUser && (
        <div className="modal-overlay" onClick={() => { setShowUserProfile(false); setViewingUser(null); }}>
          <div className="modal-panel-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="heading-section text-xl">User Profile</h3>
              <button
                onClick={() => {
                  setShowUserProfile(false);
                  setViewingUser(null);
                }}
                className="btn-icon"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* User Header */}
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {viewingUser.avatar_url ? (
                    <img className="h-20 w-20 rounded-full object-cover" src={viewingUser.avatar_url} alt="" />
                  ) : (
                    <div className="h-20 w-20 rounded-full bg-gradient-brand-tile flex items-center justify-center">
                      <span className="text-white text-2xl font-bold">
                        {viewingUser.username.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-2xl font-bold text-white">{viewingUser.username}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getRoleBadgeColor(viewingUser.role === 'super_admin' ? 'super_admin' : 'user')}`}>
                      {formatRoleLabel(viewingUser.role === 'super_admin' ? 'super_admin' : 'user')}
                    </span>
                    {hasCap(viewingUser.can_organize) ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium text-white bg-blue-600">Organizer</span>
                    ) : null}
                    {hasCap(viewingUser.can_judge) ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium text-white bg-amber-600">Judge</span>
                    ) : null}
                    {viewingUser.id === currentUser?.id && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-900/30 text-orange-400 border border-orange-700/60">
                        You
                      </span>
                    )}
                    {viewingUser.id === 1 && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-900/30 text-red-400 border border-red-700">
                        Root
                      </span>
                    )}
                  </div>
                  <p className="text-gray-400 mt-1">{viewingUser.email}</p>
                  {viewingUser.first_name && viewingUser.last_name && (
                    <p className="text-gray-300 mt-1">{viewingUser.first_name} {viewingUser.last_name}</p>
                  )}
                </div>
              </div>

              {/* User Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#161616] rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <div>
                      <p className="text-2xl font-bold text-white">{viewingUser.vehicle_count}</p>
                      <p className="text-sm text-gray-400">Vehicles</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#161616] rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H7a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                    </svg>
                    <div>
                      <p className="text-2xl font-bold text-white">{viewingUser.modification_count}</p>
                      <p className="text-sm text-gray-400">Modifications</p>
                    </div>
                  </div>
                </div>
                <div className="bg-[#161616] rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-8 h-8 text-orange-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-white">
                        {viewingUser.is_active ? (
                          <span className="text-green-400">● Active</span>
                        ) : (
                          <span className="text-red-400">● Inactive</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-400">Status</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Details */}
              <div className="bg-[#161616] rounded-lg p-4">
                <h5 className="text-lg font-semibold text-white mb-3">Account Details</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">User ID:</span>
                    <span className="ml-2 text-white">#{viewingUser.id}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Joined:</span>
                    <span className="ml-2 text-white">
                      {new Date(viewingUser.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Last Login:</span>
                    <span className="ml-2 text-white">
                      {viewingUser.last_login 
                        ? new Date(viewingUser.last_login).toLocaleDateString()
                        : 'Never'
                      }
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Account Type:</span>
                    <span className="ml-2 text-white">{formatRoleLabel(viewingUser.role)}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-white/[0.08]">
                {canModifyUser(viewingUser) && (
                  <>
                    <button
                      onClick={() => {
                        setShowUserProfile(false);
                        setSelectedUser(viewingUser);
                        setShowDeleteModal(true);
                      }}
                      className="btn-danger"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete User
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 